import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { uiSchema, translateSchema, addOptionsToSchema } from './config';
import BuilderForm from '../FormCarbon';
import NotFound from '../NotFound';
import DataTable from '../DataTable';
import { postTempConfigFile, setTempConfigFile } from '../../../actions';
import funcTranslateHelper from '../../../i18next/funcTranslateHelper';
import { Tabs, Tab } from 'carbon-components-react';

const Transitions = ({ configFile, tempConfigFile, saveTempConfigFile }) => {
  const [open, setOpen] = React.useState(false);
  const [notification, setNotification] = React.useState(null);
  const [formData, setFormData] = React.useState({});
  const schema = useMemo(translateSchema, []);

  const config = tempConfigFile || configFile;
  const defaultActions = config.defaultActions || [];
  const roles = config.roles || [];
  const rowsTransitions = [];
  const rowsAutomaticTransitions = [];
  config.transitions.forEach(t => {
    if (t.isAutomatedTransition) rowsAutomaticTransitions.push(parseTransition(t));
    else rowsTransitions.push(parseTransition(t));
  });
  const disableBuilder = config.disableBuilder;

  const headers = [
    { header: 'Nombre', key: 'name' },
    { header: 'Origen', key: 'origin' },
    { header: 'Destino', key: 'destination' },
    { header: 'Roles', key: 'roles' },
    { header: 'Acciones', key: 'actions' }
  ];

  addOptionsToSchema(schema, config);

  function findNameOfStage (name) {
    let value = '';
    name !== 'none'
      ? config.stages.forEach(rootStages =>
        rootStages.stages.forEach(subStages => {
          if (subStages.id === name && subStages.name !== undefined) { value = `${rootStages.name}.${subStages.name}`; }
        })
      )
      : (value = 'none');
    return value;
  }

  function parseTransition (transition) {
    return {
      id: transition.id,
      name: transition.nombre,
      origin: findNameOfStage(transition.source),
      destination: findNameOfStage(transition.destination),
      roles: transition.roles,
      actions: transition.actions.find(a => a.mandatory).name
    };
  }

  function findObjectAction (formData) {
    const selectedActionId = formData.actions;
    const actionObject = config.actions.filter(
      action => action.id === selectedActionId
    );
    return actionObject;
  }

  const toggleForm = () => {
    setOpen(!open);
    if (open) setFormData({});
  };

  const onAdd = ({ formData }) => {
    formData.id = new Date().getTime().toString();
    formData.actions = findObjectAction(formData);

    if (formData.hasAutomatedTransition || (formData.additionalActions && formData.additionalActions.length)) {
      formData.actions = formData.actions.concat(generateAdditionalAction(formData));
    }

    const configFile = {
      ...config,
      transitions: [...config.transitions, formData]
    };

    saveConfig(configFile);
  };

  const onDelete = id => {
    const configFile = {
      ...config,
      transitions: config.transitions.filter(transition => transition.id !== id)
    };
    saveConfig(configFile);
  };

  const openEdit = id => {
    const transition = {
      ...config.transitions.filter(transition => transition.id === id)[0]
    };
    transition.actions = transition.actions[0].id;
    setFormData(transition);
    toggleForm();
  };

  const onEdit = ({ formData }) => {
    formData.actions = findObjectAction(formData);

    if (formData.hasAutomatedTransition || (formData.additionalActions && formData.additionalActions.length)) {
      formData.actions = formData.actions.concat(generateAdditionalAction(formData));
    }

    const configFile = {
      ...config,
      transitions: config.transitions.map(transition =>
        transition.id === formData.id ? formData : transition
      )
    };
    saveConfig(configFile);
  };

  const saveConfig = async config => {
    const res = await postTempConfigFile(config);
    if (res.message === 'OK') {
      saveTempConfigFile(config);
      setNotification({
        type: 'success',
        message: funcTranslateHelper('Builder:Message:realizado')
      });
    } else {
      setNotification({
        type: 'error',
        message: funcTranslateHelper('Builder:Message:error')
      });
    }

    setTimeout(() => setNotification(null), 2000);
    if (open) toggleForm();
  };

  const generateAdditionalAction = formData => {
    const actions = [];

    if (formData.additionalActions && formData.additionalActions.length > 0) {
      actions.additionalActions = formData.additionalActions.reduce((acc, additionalActionId) => {
        const defaultAction = defaultActions.find(e => e.id === additionalActionId);

        acc.actions = acc.actions.concat([additionalActionId]);
        if (defaultAction.schema) {
          acc.schema = {
            type: 'object',
            properties: {
              ...acc.schema.properties,
              [defaultAction.id]: {
                title: funcTranslateHelper(`Builder:Transitions:form:${defaultAction.title}`),
                ...defaultAction.schema
              }
            }
          };
        }

        if (defaultAction.uiSchema) {
          acc.uiSchema = {
            [defaultAction.id]: {
              ...defaultAction.uiSchema
            }
          };
        }

        return acc;
      }, { id: 'additionalActions', title: 'Acciones Adicionales', actions: [], schema: { properties: {} } });
    }

    if (formData.hasAutomatedTransition && formData.automatedTransition && formData.automatedTransition.length) {
      const automaticTransition = config.transitions.find(t => t.id === formData.automatedTransition[0].id);
      const defaultAction = defaultActions.find(e => e.id === 'expiration');

      actions.automaticAction = {
        id: 'automaticAction',
        actions: ['expiration'],
        title: automaticTransition.nombre || defaultAction.title,
        schema: {
          properties: {
            expiration: {
              ...defaultAction.schema,
              title: '',
              properties: {
                ...defaultAction.schema.properties,
                transitionId: {
                  type: 'string',
                  default: formData.automatedTransition[0].id
                }
              }
            }
          }
        },
        uiSchema: {
          expiration: {
            transitionId: {
              'ui:widget': 'hidden',
              'ui:readonly': true
            }
          }
        }
      };

      const additionalActions = automaticTransition.actions.find(a => a.id === 'additionalActions');

      if (additionalActions) {
        actions.automaticAction.schema.properties.expiration.properties.transitionFormData = {
          type: 'object',
          title: funcTranslateHelper('Builder:Transitions:form:additionalAutoActions'),
          properties: {
            ...additionalActions.schema.properties
          }
        };
      }
    }

    return Object.values(actions);
  };

  if (open) {
    return (
      <BuilderForm
        onAdd={onAdd}
        onClose={toggleForm}
        onEdit={onEdit}
        open={open}
        title={funcTranslateHelper('Builder.Transitions.form.titulo')}
        subtitle={funcTranslateHelper('Builder.Transitions.form.subtitulo')}
        schema={schema}
        uiSchema={uiSchema}
        formData={formData}
        primaryButtonText={funcTranslateHelper('Guardar')}
        secondaryButtonText={funcTranslateHelper('Cancelar')}
        Template='RowTemplate'
        validation
      />
    );
  }

  if (!rowsTransitions.length && !rowsAutomaticTransitions.length) {
    return (
      <NotFound
        title={funcTranslateHelper('Builder.Transitions.titulo')}
        // subtitle={funcTranslateHelper('Builder.Transitions.subtitulo')}
        handleOpen={toggleForm}
        notification={notification}
        message={funcTranslateHelper('Builder:Transitions:addTransition')}
      />
    );
  }

  return (
    <div className='data-table-container'>
      <Tabs>
        <Tab
          label={funcTranslateHelper('Builder:Transitions:titulo')}
          tabIndex={0}
        >
          {!rowsTransitions.length ? (
            <NotFound
              title={funcTranslateHelper('Builder.Transitions.titulo')}
              // subtitle={funcTranslateHelper('Builder.Transitions.subtitulo')}
              handleOpen={toggleForm}
              notification={notification}
              message={funcTranslateHelper('Builder:Transitions:addTransition')}
            />
          ) : (
            <DataTable
              title={funcTranslateHelper('Builder.Transitions.titulo')}
              // subtitle={funcTranslateHelper('Builder.Transitions.subtitulo')}
              headers={headers}
              rows={rowsTransitions}
              handleOpen={toggleForm}
              onDelete={onDelete}
              onEdit={openEdit}
              allowEdit={!disableBuilder}
              rawData={roles}
              notification={notification}
            />
          )}
        </Tab>
        <Tab
          label={funcTranslateHelper('Builder:Transitions:autoTransitions')}
          tabIndex={1}
        >
          {!rowsAutomaticTransitions.length ? (
            <NotFound
              title={funcTranslateHelper('Builder.Transitions.automaticTransitionsTitle')}
              // subtitle={funcTranslateHelper('Builder.Transitions.subtitulo')}
              handleOpen={toggleForm}
              notification={notification}
              message={funcTranslateHelper('Builder:Transitions:addTransition')}
            />
          ) : (
            <DataTable
              title={funcTranslateHelper('Builder.Transitions.automaticTransitionsTitle')}
              subtitle={funcTranslateHelper('Builder.Transitions.automaticTransitionsSubtitle')}
              headers={headers}
              rows={rowsAutomaticTransitions}
              handleOpen={toggleForm}
              onDelete={onDelete}
              onEdit={openEdit}
              allowEdit={!disableBuilder}
              rawData={roles}
              notification={notification}
            />
          )}
        </Tab>
      </Tabs>

    </div>
  );
};

const mapStateToProps = state => {
  return {
    configFile: state.configFile,
    tempConfigFile: state.tempConfigFile
  };
};

const mapDispatchToProps = dispatch => {
  return {
    saveTempConfigFile: data => dispatch(setTempConfigFile(data))
  };
};

Transitions.propTypes = {
  configFile: PropTypes.object.isRequired,
  tempConfigFile: PropTypes.object.isRequired,
  saveTempConfigFile: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(Transitions);
